import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Hook reutilizable para la preferencia de "columnas visibles" de una tabla.
//
// Encapsula exactamente la misma lógica que usan las tablas de Postulaciones y
// Formularios: una copia inmediata en localStorage (respaldo por navegador) y un
// guardado remoto con rebote hacia el endpoint `table-preferences` de APPLICANT
// (cyber-service), que persiste la preferencia por usuario y por tabla.
//
// La preferencia enumera EXACTAMENTE las columnas visibles, así que una columna
// nueva no aparece sola para quien ya guardó una selección anterior. Cada tabla
// que agregue columnas debe revelarlas con su propia migración (ver Postulaciones),
// subiendo `migrationVersion` y pasando las claves a revelar en `columnsToReveal`.

export interface TableColumnLike {
  key: string;
}

interface UseTableColumnPreferenceOptions<TKey extends string> {
  /** Clave de tabla que entiende el backend (p. ej. "SELECCION_MASIVA_POR_VALIDAR"). */
  table: string;
  /** Prefijo del storageKey local, p. ej. "seleccion-masiva-por-validar". */
  storageNamespace: string;
  /** Definición canónica y ordenada de las columnas configurables. */
  columnDefinitions: readonly { key: TKey }[];
  /** Id del usuario autenticado; sin él solo se usa localStorage. */
  loggedUserId: string;
  /** Base URL del servicio APPLICANT (process.env.REACT_APP_API_APPLICANT). */
  baseUrl?: string;
  /** Versión de migración; al subirla se revelan `columnsToReveal` una sola vez. */
  migrationVersion?: string;
  /** Claves a revelar en la migración vigente (solo las nuevas). */
  columnsToReveal?: readonly TKey[];
}

interface UseTableColumnPreferenceResult<TKey extends string> {
  visibleColumnKeys: TKey[];
  setVisibleColumnKeys: (keys: TKey[]) => void;
  isSaving: boolean;
  isSynchronized: boolean | null;
}

const allKeysOf = <TKey extends string>(
  columnDefinitions: readonly { key: TKey }[],
): TKey[] => columnDefinitions.map(({ key }) => key);

const normalizeVisibleColumns = <TKey extends string>(
  value: unknown,
  columnDefinitions: readonly { key: TKey }[],
): TKey[] | null => {
  if (!Array.isArray(value) || value.length === 0) return null;
  const keys = allKeysOf(columnDefinitions).filter((key) => value.includes(key));
  return keys.length === value.length ? keys : null;
};

const readVisibleColumns = <TKey extends string>(
  storageKey: string,
  columnDefinitions: readonly { key: TKey }[],
): TKey[] => {
  try {
    return (
      normalizeVisibleColumns(
        JSON.parse(localStorage.getItem(storageKey) ?? "[]"),
        columnDefinitions,
      ) ?? allKeysOf(columnDefinitions)
    );
  } catch {
    return allKeysOf(columnDefinitions);
  }
};

const withColumnsVisible = <TKey extends string>(
  keys: TKey[],
  columnDefinitions: readonly { key: TKey }[],
  extraKeys: readonly TKey[],
): TKey[] => {
  const visibleKeys = new Set<TKey>([...keys, ...extraKeys]);
  return allKeysOf(columnDefinitions).filter((key) => visibleKeys.has(key));
};

export function useTableColumnPreference<TKey extends string>({
  table,
  storageNamespace,
  columnDefinitions,
  loggedUserId,
  baseUrl = process.env.REACT_APP_API_APPLICANT,
  migrationVersion,
  columnsToReveal,
}: UseTableColumnPreferenceOptions<TKey>): UseTableColumnPreferenceResult<TKey> {
  const columnsStorageKey = useMemo(
    () => `teamwork:${storageNamespace}:visible-columns:${loggedUserId || "anonymous"}`,
    [loggedUserId, storageNamespace],
  );
  const migrationKey = useMemo(
    () =>
      migrationVersion
        ? `teamwork:${storageNamespace}:columns-migration:${loggedUserId || "anonymous"}:${migrationVersion}`
        : null,
    [loggedUserId, migrationVersion, storageNamespace],
  );

  const migrationPendingRef = useRef(false);

  const applyPendingMigration = useCallback(
    (keys: TKey[]): TKey[] => {
      if (!migrationKey || !columnsToReveal || columnsToReveal.length === 0) return keys;
      try {
        if (localStorage.getItem(migrationKey) === "complete") return keys;
      } catch {
        // Si localStorage falla, se intenta la migración igual.
      }
      migrationPendingRef.current = true;
      return withColumnsVisible(keys, columnDefinitions, columnsToReveal);
    },
    [columnDefinitions, columnsToReveal, migrationKey],
  );

  const [visibleColumnKeys, setVisibleColumnKeys] = useState<TKey[]>(() =>
    applyPendingMigration(readVisibleColumns(columnsStorageKey, columnDefinitions)),
  );
  const [columnPreferenceLoaded, setColumnPreferenceLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSynchronized, setIsSynchronized] = useState<boolean | null>(null);

  // Carga remota: la preferencia del servidor manda sobre la copia local.
  useEffect(() => {
    let cancelled = false;

    if (!baseUrl || !loggedUserId) {
      setColumnPreferenceLoaded(true);
      return;
    }

    const params = new URLSearchParams({ userId: loggedUserId, table });

    fetch(`${baseUrl}/application/table-preferences?${params.toString()}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        setIsSynchronized(true);
        const normalizedRemoteColumns = normalizeVisibleColumns(
          data?.preference?.visibleColumns,
          columnDefinitions,
        );
        const remoteColumns = normalizedRemoteColumns
          ? applyPendingMigration(normalizedRemoteColumns)
          : normalizedRemoteColumns;
        if (remoteColumns) {
          setVisibleColumnKeys(remoteColumns);
          try {
            localStorage.setItem(columnsStorageKey, JSON.stringify(remoteColumns));
          } catch {
            // La preferencia remota sigue disponible aunque localStorage esté bloqueado.
          }
        }
      })
      .catch(() => {
        if (!cancelled) setIsSynchronized(false);
      })
      .finally(() => {
        if (!cancelled) setColumnPreferenceLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [applyPendingMigration, baseUrl, columnDefinitions, columnsStorageKey, loggedUserId, table]);

  // Guardado: copia local inmediata + POST remoto con rebote.
  useEffect(() => {
    try {
      localStorage.setItem(columnsStorageKey, JSON.stringify(visibleColumnKeys));
    } catch {
      // La tabla sigue funcionando aunque el navegador bloquee localStorage.
    }
    if (!columnPreferenceLoaded || !loggedUserId || !baseUrl) return;

    let controller: AbortController | null = null;
    const saveTimer = window.setTimeout(() => {
      controller = new AbortController();
      setIsSaving(true);
      const params = new URLSearchParams({ userId: loggedUserId, table });
      fetch(`${baseUrl}/application/table-preferences?${params.toString()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visibleColumns: visibleColumnKeys }),
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          setIsSynchronized(true);
          if (migrationPendingRef.current && migrationKey) {
            try {
              localStorage.setItem(migrationKey, "complete");
              migrationPendingRef.current = false;
            } catch {
              // La columna seguirá visible aunque el navegador bloquee localStorage.
            }
          }
        })
        .catch((requestError) => {
          if (requestError?.name !== "AbortError") {
            setIsSynchronized(false);
          }
        })
        .finally(() => {
          if (!controller?.signal.aborted) setIsSaving(false);
        });
    }, 350);

    return () => {
      window.clearTimeout(saveTimer);
      controller?.abort();
    };
  }, [baseUrl, columnPreferenceLoaded, columnsStorageKey, loggedUserId, migrationKey, table, visibleColumnKeys]);

  return { visibleColumnKeys, setVisibleColumnKeys, isSaving, isSynchronized };
}
